// pages/Dashboard/InvoiceView.js
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaPrint,
  FaArrowLeft,
  FaEdit,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCheckCircle,
} from 'react-icons/fa';
import '../Css/InvoiceView.css';

import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl';

const PAYMENT_LABELS = {
  cash: 'Cash',
  upi: 'UPI',
  qr: 'QR Code',
  card: 'Card',
};

const STATUS_STYLES = {
  paid: { label: 'Paid', cls: 'status-paid' },
  partial: { label: 'Partial', cls: 'status-partial' },
  due: { label: 'Due', cls: 'status-due' },
};

function InvoiceView() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const printRef = useRef(null);

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${baseurl}/invoices/${uuid}`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to fetch invoice');
        setInvoice(data.data.invoice);
      } catch (err) {
        toast(err.message || 'Failed to fetch invoice', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [uuid]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div className="invoice-view-loading">Loading invoice...</div>;
  }

  if (!invoice) {
    return <div className="invoice-view-loading">Invoice not found</div>;
  }

  const subtotal = Number(invoice.subtotal);
  const discountValue = Number(invoice.discount_value);
  const manualDiscount =
    invoice.discount_type === 'percent' ? (subtotal * discountValue) / 100 : discountValue;
  const couponDiscount = invoice.coupon_percent
    ? (subtotal * Number(invoice.coupon_percent)) / 100
    : 0;
  const taxAmount = Number(invoice.tax_amount);
  const grandTotal = Number(invoice.grand_total);
  const totalPaid = Number(invoice.total_paid);
  const dueAmount = Number(invoice.due_amount);

  return (
    <div className="invoice-view-page">
      {/* TOP BAR (hidden on print) */}
      <div className="invoice-view-topbar no-print">
        <button className="btn-outline" onClick={() => navigate('/dashboard/invoices')}>
          <FaArrowLeft /> Back to Invoices
        </button>
        <div className="topbar-actions">
          {!invoice.is_archived && (
            <button
              className="btn-outline"
              onClick={() => navigate(`/dashboard/billing/${uuid}?action=edit`)}
            >
              <FaEdit /> Edit Invoice
            </button>
          )}
          <button className="btn-primary" onClick={handlePrint}>
            <FaPrint /> Print / Download
          </button>
        </div>
      </div>

      {/* PRINTABLE INVOICE */}
      <div className="invoice-sheet" ref={printRef}>
        {/* HEADER */}
        <div className="invoice-sheet-header">
          <div className="brand-block">
            <div className="brand-logo">SP</div>
            <div>
              <h2>Salon & Spa</h2>
              <p className="brand-tagline">Beauty. Wellness. Confidence.</p>
            </div>
          </div>

          <div className="invoice-meta-block">
            <h1>INVOICE</h1>
            <p className="invoice-number">{invoice.invoice_number}</p>
            <span className={`status-pill ${STATUS_STYLES[invoice.status]?.cls}`}>
              {invoice.status === 'paid' && <FaCheckCircle />}
              {STATUS_STYLES[invoice.status]?.label}
            </span>
          </div>
        </div>

        <div className="invoice-sheet-divider"></div>

        {/* BILL TO + INVOICE INFO */}
        <div className="invoice-info-row">
          <div className="bill-to-block">
            <span className="block-label">Billed To</span>
            <h3>{invoice.client_name}</h3>
            {invoice.client_phone && (
              <p className="bill-to-line">
                <FaPhoneAlt /> {invoice.client_phone}
              </p>
            )}
          </div>

          <div className="invoice-dates-block">
            <div className="date-row">
              <span className="block-label">Invoice Date</span>
              <strong>
                {new Date(invoice.created_at).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </strong>
            </div>
            <div className="date-row">
              <span className="block-label">Invoice ID</span>
              <strong>#{invoice.id}</strong>
            </div>
          </div>
        </div>

        {/* ITEMS TABLE */}
        <div className="invoice-items-section">
          <table className="invoice-items-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Staff</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item) => (
                <tr key={item.id}>
                  <td>{item.service_name}</td>
                  <td>{item.staff_name}</td>
                  <td>{item.qty}</td>
                  <td>₹{Number(item.price).toLocaleString()}</td>
                  <td className="cell-total">₹{Number(item.total).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* SUMMARY */}
        <div className="invoice-summary-section">
          <div className="invoice-summary-box">
            <div className="summary-line">
              <span>Subtotal</span>
              <span>₹{subtotal.toLocaleString()}</span>
            </div>
            {manualDiscount > 0 && (
              <div className="summary-line discount">
                <span>Discount</span>
                <span>-₹{manualDiscount.toFixed(2)}</span>
              </div>
            )}
            {couponDiscount > 0 && (
              <div className="summary-line discount">
                <span>Coupon ({invoice.coupon_code})</span>
                <span>-₹{couponDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="summary-line">
              <span>Tax ({invoice.tax_percent}%)</span>
              <span>₹{taxAmount.toFixed(2)}</span>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-line grand-total">
              <span>Grand Total</span>
              <span>₹{grandTotal.toFixed(2)}</span>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-line">
              <span>Amount Paid</span>
              <span>₹{totalPaid.toFixed(2)}</span>
            </div>
            {dueAmount > 0 && (
              <div className="summary-line due">
                <span>Balance Due</span>
                <span>₹{dueAmount.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* PAYMENT METHODS */}
        {invoice.payments?.length > 0 && (
          <div className="invoice-payments-section">
            <span className="block-label">Payment Methods</span>
            <div className="payment-chips">
              {invoice.payments.map((p) => (
                <span key={p.id} className="payment-chip">
                  {PAYMENT_LABELS[p.method]}: ₹{Number(p.amount).toLocaleString()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div className="invoice-sheet-footer">
          <p>Thank you for choosing us. We look forward to seeing you again!</p>
          <p className="footer-small">This is a system-generated invoice.</p>
        </div>
      </div>
    </div>
  );
}

export default InvoiceView;